import type { Root } from "../../../../../supabase/functions/sync-control/model";
import { publicJwk } from "../../../../../supabase/functions/sync-control/policy";
import type { SyncControlClient } from "./controlClient";
import {
  base64url,
  type CipherObject,
  canonical,
  openDeviceEnvelope,
  openRecoveryFile,
  sealDeviceEnvelope,
  signChallenge,
  unwrapDataKeyBytes,
  verifyChallenge,
} from "./cryptoV2";
import type { UnlockedDevice } from "./deviceVault";
import { createPairTicket, openPairMessage, type PairTicket, sealPairMessage } from "./pairing";
import { dataKeyScope } from "./syncScopes";

export type PairCandidate = { id: string; label: string; signing: JsonWebKey; agreement: JsonWebKey };
type PairRequest = { pairId: string; device: PairCandidate; signature: string };
type Approval = {
  pairId: string;
  toDeviceId: string;
  fromDeviceId: string;
  root: Pick<Root, "datasetId" | "generation" | "keyVersion">;
  keyEnvelope: Awaited<ReturnType<typeof sealDeviceEnvelope>>;
  signature: string;
};
export class PairSession {
  private request?: PairRequest;
  private constructor(
    readonly api: SyncControlClient,
    readonly ticket: PairTicket,
  ) {}
  static async create(api: SyncControlClient, authorized: boolean) {
    const result = await api.request<{ id: string; expiresAt: number }>("pair-create", {
      device: await api.publicDevice(),
    });
    return new PairSession(
      api,
      await createPairTicket(result.id, result.expiresAt, authorized ? "authorized-displays" : "new-displays"),
    );
  }
  static join(api: SyncControlClient, ticket: PairTicket) {
    return new PairSession(api, ticket);
  }
  async requestJoin() {
    const device = { id: this.api.device.deviceId, ...(await this.api.publicDevice()) };
    const body = { pairId: this.ticket.id, device };
    const envelope = await sealPairMessage(this.ticket, "request", {
      ...body,
      signature: await signChallenge(this.api.device.signing.privateKey, body),
    });
    await this.api.request("pair-request", { device: await this.api.publicDevice(), pairId: this.ticket.id, envelope });
  }
  async read() {
    const response = await this.api.request<{
      request: CipherObject | null;
      approval: CipherObject | null;
      expiresAt: number;
    }>("pair-read", { device: await this.api.publicDevice(), pairId: this.ticket.id });
    if (response.expiresAt !== this.ticket.expiresAt) throw new Error("sync-pair-expired");
    return response;
  }
  async candidate() {
    const response = await this.read();
    if (!response.request) return null;
    const body = (await openPairMessage(this.ticket, "request", response.request)) as PairRequest;
    if (
      body?.pairId !== this.ticket.id ||
      !body.device ||
      typeof body.device.id !== "string" ||
      !/^[-\w]{1,128}$/.test(body.device.id) ||
      typeof body.device.label !== "string" ||
      body.device.label.length > 100
    )
      throw new Error("sync-pair-format");
    const key = await crypto.subtle.importKey(
      "jwk",
      publicJwk(body.device.signing),
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    publicJwk(body.device.agreement);
    if (!(await verifyChallenge(key, { pairId: body.pairId, device: body.device }, body.signature)))
      throw new Error("sync-device-signature");
    this.request = body;
    return body.device;
  }
  async approve(device: UnlockedDevice) {
    if (!this.request) throw new Error("sync-pair-format");
    const summary = await this.api.devices();
    const root = summary.root;
    if (
      !root ||
      root.datasetId !== device.scope.datasetId ||
      root.generation !== device.scope.generation ||
      root.keyVersion !== device.scope.keyVersion
    )
      throw new Error("sync-generation-mismatch");
    const material = await unwrapDataKeyBytes(device.wrapping, device.scope, device.wrapped);
    const recipient = this.request.device;
    const keyEnvelope = await sealDeviceEnvelope(
      recipient.agreement,
      { ...dataKeyScope(root), objectId: recipient.id },
      { dataKey: base64url(material) },
    );
    material.fill(0);
    const body = {
      pairId: this.ticket.id,
      toDeviceId: recipient.id,
      fromDeviceId: device.deviceId,
      root: { datasetId: root.datasetId, generation: root.generation, keyVersion: root.keyVersion },
      keyEnvelope,
    };
    const envelope = await sealPairMessage(this.ticket, "approval", {
      ...body,
      signature: await signChallenge(device.signing.privateKey, body),
    });
    const target = {
      pairId: this.ticket.id,
      device: recipient,
      envelope,
      keyEnvelope,
      datasetId: root.datasetId,
      generation: root.generation,
      keyVersion: root.keyVersion,
    };
    await this.api.request("pair-approve", target);
  }
  async approval() {
    const response = await this.read();
    if (!response.approval) return null;
    const result = (await openPairMessage(this.ticket, "approval", response.approval)) as Approval;
    const summary = await this.api.devices();
    const root = summary.root;
    const sender = summary.devices.find((d) => d.id === result?.fromDeviceId && !d.revokedAt);
    if (
      !root ||
      !sender ||
      result.toDeviceId !== this.api.device.deviceId ||
      result.pairId !== this.ticket.id ||
      canonical(result.root) !==
        canonical({ datasetId: root.datasetId, generation: root.generation, keyVersion: root.keyVersion }) ||
      canonical(root.deviceEnvelopes[this.api.device.deviceId]) !== canonical(result.keyEnvelope)
    )
      throw new Error("sync-pair-format");
    const publicKey = await crypto.subtle.importKey(
      "jwk",
      sender.signing,
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
    const { signature, ...body } = result;
    if (!(await verifyChallenge(publicKey, body, signature))) throw new Error("sync-device-signature");
    const material = (await openDeviceEnvelope(
      this.api.device.agreement.privateKey,
      { ...dataKeyScope(root), objectId: this.api.device.deviceId },
      result.keyEnvelope,
    )) as { dataKey: string };
    return { root, material: material.dataKey };
  }
  async complete() {
    try {
      await this.api.request("pair-complete", { pairId: this.ticket.id });
    } catch (error) {
      // The local join has already committed. An expired relay needs no
      // cleanup and must not leave the UI offering to replace the data again.
      if (!(error instanceof Error) || error.message !== "sync-pair-expired") throw error;
    }
  }
}

export async function recoverDevice(api: SyncControlClient, file: string, password: string, resetVerification = false) {
  const material = await openRecoveryFile(file, password);
  const device = await api.publicDevice();
  const response = await api.request<{ challenge: unknown; root: Root }>("recover-options", {
    device,
    resetVerification,
  });
  const root = response.root;
  if (
    root.datasetId !== material.datasetId ||
    root.generation !== material.generation ||
    root.keyVersion !== material.keyVersion ||
    canonical(
      publicJwk({
        kty: material.recoveryPrivateKey.kty,
        crv: material.recoveryPrivateKey.crv,
        x: material.recoveryPrivateKey.x,
        y: material.recoveryPrivateKey.y,
      }),
    ) !== canonical(publicJwk(root.recoveryPublicKey))
  )
    throw new Error("sync-recovery-file-mismatch");
  const incoming = { id: api.device.deviceId, ...device, createdAt: 0 };
  const signing = await crypto.subtle.importKey(
    "jwk",
    material.recoveryPrivateKey,
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"],
  );
  const recoverySignature = await signChallenge(signing, {
    accountId: api.device.accountId,
    challenge: response.challenge,
    device: incoming,
  });
  const keyEnvelope = await sealDeviceEnvelope(
    device.agreement,
    { ...dataKeyScope(root), objectId: incoming.id },
    { dataKey: material.dataKey },
  );
  const result = await api.request<{ reenrollProof: { id: string }; reauthenticate: boolean }>("recover-verify", {
    resetVerification,
    device,
    challengeId: (response.challenge as { id: string }).id,
    recoverySignature,
    keyEnvelope,
  });
  return {
    root,
    material: material.dataKey,
    reenrollProof: result.reenrollProof.id,
    reauthenticate: result.reauthenticate,
  };
}
